/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
import { useEffect, useRef, useState } from 'react';
import GetInvolved from './sections/GetInvolved';
import NewsFeed from './sections/NewsFeed';
import Banner from './sections/PageBanner';
import Footer from './sections/PageFooter';
import PageHeader from './sections/PageHeader';
import Hero from './sections/PageHero';
import GlobeMap from '../home/Dashboard/EarthGlobe';
import axios from '../../utils/axios';
import HeroCarousel from './sections/HeroCarousel';
import ReactGA from 'react-ga4';

ReactGA.initialize('G-FMCF2WQ3F6');

/* ─── Scroll-reveal hook ───────────────────────────────────────────── */
function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

/* ─── Why WITS Matters ─────────────────────────────────────────────── */
function WhyItMatters() {
  const { ref, visible } = useReveal();
  return (
    <section className="bg-[#fafaf7] overflow-hidden">
      <div
        ref={ref}
        className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center"
      >
        {/* Text */}
        <div className={`reveal ${visible ? 'visible' : ''}`}>
          <span className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-green-600" />
            <span
              className="text-green-700 text-xs tracking-[0.2em] uppercase font-medium"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Why It Matters
            </span>
          </span>

          <h2
            className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-8"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            From Anecdote{' '}
            <span className="text-green-700">to Evidence</span>
          </h2>

          <p
            className="text-gray-600 text-lg leading-relaxed mb-5"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Africa loses an elephant every 15 minutes to poaching. A rhinoceros, every 8 hours.
            Yet fewer than 5% of wildlife crime seizures ever result in a conviction. The data
            exists &mdash; it just hasn&apos;t been connected.
          </p>

          <p
            className="text-gray-500 leading-relaxed mb-10"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            WITS transforms fragmented incident reports into a unified intelligence layer —
            enabling prosecutors to build stronger cases, rangers to identify patterns before
            crimes occur, and policymakers to drive structural reform backed by irrefutable
            evidence.
          </p>

          <a
            href="/about-us"
            className="inline-flex items-center gap-2 text-green-700 font-semibold text-sm border-b-2 border-green-700 pb-0.5 hover:text-green-900 hover:border-green-900 transition-all"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Read Our Mission →
          </a>
        </div>

        {/* Image */}
        <div className={`reveal reveal-delay-2 ${visible ? 'visible' : ''} relative`}>
          <div className="aspect-[4/5] overflow-hidden rounded-2xl shadow-2xl shadow-gray-200">
            <img
              src="./why-it-matters.jpg"
              alt="Conservation fieldwork in Africa"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>
          {/* Floating callout */}
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-green-50 max-w-[160px]">
            <p
              className="text-3xl font-bold text-green-700"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              96%
            </p>
            <p
              className="text-xs text-gray-400 mt-1 leading-snug"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              of major wildlife crimes go unpunished
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Platform Features ─────────────────────────────────────────────── */
const features = [
  {
    icon:  '🗂️',
    title: 'Case Management',
    desc:  'Track case progress from first incident report to final verdict. Manage evidence chains, offender profiles, and court timelines with secure, tamper-evident audit trails.',
    href:  '/dashboard',
  },
  {
    icon:  '🔍',
    title: 'Pattern Intelligence',
    desc:  'Our analytics engine surfaces hidden connections between isolated incidents — exposing trafficking networks and syndicate structures invisible to any single agency.',
    href:  '/dashboard',
  },
  {
    icon:  '🗺️',
    title: 'Geographic Mapping',
    desc:  'Interactive heatmaps and layered geospatial views reveal crime corridors, hotspots, and smuggling routes with satellite-grade precision.',
    href:  '/dashboard',
  },
  {
    icon:  '📡',
    title: 'Incident Reporting',
    desc:  'Field officers and communities submit verified reports in real time — with photo evidence, GPS coordinates, and species identification built right in.',
    href:  '/report-incident',
  },
  {
    icon:  '⚖️',
    title: 'Legal Intelligence',
    desc:  'A searchable database of case precedents, prosecution strategies, and sentencing benchmarks to empower wildlife crime prosecutors at every level.',
    href:  '/case-precedents',
  },
  {
    icon:  '📈',
    title: 'Policy Analytics',
    desc:  'Generate evidence-ready reports for national governments, CITES, and international conservation bodies — backed by verified field data.',
    href:  '/dashboard',
  },
];

function Features() {
  const { ref, visible } = useReveal();
  return (
    <section className="bg-[#052e16] py-24 lg:py-32 overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className={`text-center mb-16 reveal ${visible ? 'visible' : ''}`}>
          <span className="inline-flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-green-400" />
            <span
              className="text-green-400 text-xs tracking-[0.22em] uppercase font-medium"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Platform Capabilities
            </span>
            <span className="h-px w-8 bg-green-400" />
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-5"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Built for the{' '}
            <span className="text-green-400">Frontlines</span>
          </h2>
          <p
            className="text-white/50 max-w-2xl mx-auto text-lg leading-relaxed"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Every feature in WITS was shaped by rangers, prosecutors, researchers, and
            community leaders operating across Africa.
          </p>
        </div>

        {/* Feature grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <a
              key={f.title}
              href={f.href}
              className={`reveal reveal-delay-${(i % 5) + 1} ${visible ? 'visible' : ''} group flex flex-col p-8 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-green-400/40 transition-all duration-500 hover:-translate-y-1 cursor-pointer`}
            >
              <span className="text-3xl mb-5 block">{f.icon}</span>
              <h3
                className="text-lg font-semibold text-white mb-3 leading-snug"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {f.title}
              </h3>
              <p
                className="text-white/50 text-sm leading-relaxed flex-1 mb-6"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                {f.desc}
              </p>
              <span
                className="text-green-400 text-sm font-medium group-hover:text-green-300 transition-colors inline-flex items-center gap-1 group-hover:gap-2 transition-all"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                Learn more →
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Connecting the Dots ───────────────────────────────────────────── */
const incidentDots = [
  { top: '30%', left: '36%', color: 'green',  delay: '0s',    label: 'Nairobi, Kenya' },
  { top: '22%', left: '52%', color: 'green',  delay: '0.5s',  label: 'Kampala, Uganda' },
  { top: '48%', left: '30%', color: 'yellow', delay: '1.0s',  label: 'Dar es Salaam' },
  { top: '60%', left: '44%', color: 'green',  delay: '0.8s',  label: 'Lusaka, Zambia' },
  { top: '18%', left: '44%', color: 'yellow', delay: '1.4s',  label: 'Addis Ababa' },
  { top: '38%', left: '58%', color: 'green',  delay: '0.3s',  label: 'Mombasa Port' },
];

function ConnectingDots() {
  const { ref, visible } = useReveal();
  return (
    <section className="bg-[#071a0e] py-24 lg:py-32 overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Section label */}
        <div className={`flex justify-center mb-14 reveal ${visible ? 'visible' : ''}`}>
          <span className="inline-flex items-center gap-3">
            <span className="h-px w-8 bg-green-500" />
            <span
              className="text-green-500 text-xs tracking-[0.22em] uppercase font-medium"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Intelligence Network
            </span>
            <span className="h-px w-8 bg-green-500" />
          </span>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">

          {/* ── Map panel ── */}
          <div className={`lg:col-span-3 reveal ${visible ? 'visible' : ''}`}>
            <div className="relative rounded-3xl overflow-hidden border border-green-900/50 bg-[#041208]"
              style={{ boxShadow: '0 0 60px rgba(22,163,74,0.12), inset 0 0 40px rgba(0,0,0,0.4)' }}
            >
              {/* Dot-grid background */}
              <div
                className="absolute inset-0 opacity-20 pointer-events-none"
                style={{
                  backgroundImage: 'radial-gradient(circle, #16a34a 1px, transparent 1px)',
                  backgroundSize: '28px 28px',
                }}
              />

              {/* Radial glow */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(22,163,74,0.08) 0%, transparent 70%)',
                }}
              />

              {/* Map SVG with green tint */}
              <div className="relative p-6 lg:p-10">
                <img
                  src="./map.svg"
                  alt="Africa wildlife crime intelligence map"
                  className="w-full relative z-10 select-none"
                  style={{
                    filter:
                      'brightness(0) invert(1) sepia(1) saturate(3) hue-rotate(95deg) brightness(0.65) opacity(0.9)',
                  }}
                />

                {/* Pulse incident dots */}
                {incidentDots.map((dot, i) => (
                  <span
                    key={i}
                    className="absolute z-20"
                    style={{ top: dot.top, left: dot.left }}
                    title={dot.label}
                  >
                    <span
                      className={`absolute inline-flex h-4 w-4 rounded-full -translate-x-1/2 -translate-y-1/2 ${
                        dot.color === 'yellow' ? 'bg-yellow-400' : 'bg-green-400'
                      } opacity-60 animate-ping`}
                      style={{ animationDelay: dot.delay, animationDuration: '2s' }}
                    />
                    <span
                      className={`relative inline-flex rounded-full h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 border-2 ${
                        dot.color === 'yellow'
                          ? 'bg-yellow-400 border-yellow-200'
                          : 'bg-green-400 border-green-200'
                      }`}
                    />
                  </span>
                ))}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-5 px-6 pb-5">
                <span className="flex items-center gap-1.5 text-[10px] text-white/35" style={{ fontFamily: 'var(--font-body)' }}>
                  <span className="inline-block w-2 h-2 rounded-full bg-green-400" />
                  Active incident
                </span>
                <span className="flex items-center gap-1.5 text-[10px] text-white/35" style={{ fontFamily: 'var(--font-body)' }}>
                  <span className="inline-block w-2 h-2 rounded-full bg-yellow-400" />
                  High-risk corridor
                </span>
                <span className="ml-auto text-[10px] text-white/20" style={{ fontFamily: 'var(--font-body)' }}>
                  Live intelligence overlay
                </span>
              </div>
            </div>
          </div>

          {/* ── Text ── */}
          <div className={`lg:col-span-2 reveal reveal-delay-2 ${visible ? 'visible' : ''}`}>
            <h2
              className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-7"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Unearthing{' '}
              <span className="text-green-400">Hidden</span>{' '}
              Connections
            </h2>

            <p
              className="text-white/55 text-base leading-relaxed mb-5"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Poaching rarely operates in isolation. A tusk seizure in Mombasa may connect
              to a network spanning Nairobi, Kampala, and Hong Kong. WITS finds those threads.
            </p>

            <p
              className="text-white/40 text-sm leading-relaxed mb-10"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              By cross-referencing incident locations, offender profiles, species data, and
              trade routes, our system surfaces patterns invisible to any single organisation —
              and puts that intelligence directly in the hands of investigators.
            </p>

            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '340+', label: 'Connected incidents per month' },
                { value: '12×',  label: 'Faster syndicate identification' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-2xl p-5 border border-green-900/50 bg-green-950/30"
                >
                  <p
                    className="text-2xl font-bold mb-1"
                    style={{ fontFamily: 'var(--font-display)', color: '#d4a84b' }}
                  >
                    {stat.value}
                  </p>
                  <p
                    className="text-xs text-white/40 leading-snug"
                    style={{ fontFamily: 'var(--font-body)' }}
                  >
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>

            <a
              href="/dashboard"
              className="mt-8 inline-flex items-center gap-2 text-green-400 text-sm font-semibold hover:text-green-300 transition-colors"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Open Live Intelligence Platform →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Globe wrapper ─────────────────────────────────────────────────── */
function GlobeSection() {
  const { ref, visible } = useReveal(0.05);
  return (
    <section className="bg-[#010b05] overflow-hidden">
      {/* Section heading */}
      <div
        ref={ref}
        className={`reveal ${visible ? 'visible' : ''} max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-6`}
      >
        <span className="flex items-center gap-3 mb-5">
          <span className="h-px w-8 bg-green-500" />
          <span
            className="text-green-500 text-xs tracking-[0.22em] uppercase font-medium"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Live Intelligence Layer
          </span>
        </span>
        <h2
          className="text-4xl lg:text-5xl font-bold text-white leading-tight"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Global Incident{' '}
          <span className="text-green-400">Map</span>
        </h2>
        <p
          className="text-white/40 mt-4 max-w-xl text-base leading-relaxed"
          style={{ fontFamily: 'var(--font-body)' }}
        >
          Wildlife crime incidents plotted worldwide. Click any point to view the case record.
        </p>
      </div>
      <GlobeMap />
    </section>
  );
}

/* ─── Page ──────────────────────────────────────────────────────────── */
export default function GuestHome() {
  ReactGA.send({ hitType: 'pageview', page: '/', title: 'Home' });

  const [news, setNews] = useState<any[]>([]);

  useEffect(() => {
    axios
      .get('/news/all')
      .then((res) => { if (res.status === 200) setNews(res.data.data); })
      .catch((_err) => { /* network error — news feed stays empty */ });
  }, []);

  return (
    <>
      <Banner />
      <PageHeader />
      <HeroCarousel />
      <Hero />
      <WhyItMatters />
      <GlobeSection />
      <Features />
      <NewsFeed newsItems={news} />
      <ConnectingDots />
      <GetInvolved />
      <Footer />
    </>
  );
}
